import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function RWD_GridView() {
  return (
    <div className="tutorial-container">
      {/* Hero Section */}
      <div
        style={{
          background: "linear-gradient(135deg, #38bdf8, #0284c7)",
          color: "white",
          padding: "2rem",
          borderRadius: "16px",
          marginBottom: "2rem",
          textAlign: "center",
        }}
      >
        <h1 style={{ fontSize: "2.5rem", fontWeight: "bold" }}>
          📐 RWD Grid View
        </h1>
        <p style={{ fontSize: "1.2rem", marginTop: "1rem" }}>
          CSS Grid makes it easy to create responsive layouts by dividing the
          page into flexible columns and rows.
        </p>
        <img
          src="https://www.w3schools.com/css/grid_lines.png"
          alt="CSS Grid Example"
          style={{
            maxWidth: "500px",
            margin: "1.5rem auto 0",
            borderRadius: "12px",
            boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
          }}
        />
      </div>

      {/* Intro */}
      <p className="tutorial-intro">
        CSS Grid is one of the most powerful tools for responsive design. With
        just a few lines of CSS, you can build layouts that adapt automatically
        to any screen size. It allows you to define rows and columns, place
        items easily, and let the browser handle responsiveness.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #bae6fd, #7dd3fc)",
          padding: "1.2rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#075985" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>CSS Grid divides a page into rows and columns.</li>
          <li>
            Responsive grids can be created using <code>auto-fit</code> or{" "}
            <code>auto-fill</code>.
          </li>
          <li>
            Use <code>minmax()</code> to set a flexible range for column sizes.
          </li>
          <li>
            Combine Grid with <code>@media</code> queries for advanced control.
          </li>
          <li>
            Grid items automatically adjust to available space, making layouts
            fluid.
          </li>
        </ul>
      </div>

      {/* Example 1: Basic Grid */}
      <h2 className="section-title">1️⃣ Basic Grid Layout</h2>
      <CodeCopyBlock
        code={`.grid-container {
  display: grid;
  grid-template-columns: auto auto auto;
  gap: 10px;
}

.grid-item {
  background: lightblue;
  padding: 20px;
  text-align: center;
}`}
      />
      <p>✅ Creates a simple 3-column grid layout.</p>

      {/* Example 2: Responsive Auto-Fit */}
      <h2 className="section-title">2️⃣ Responsive Auto-Fit</h2>
      <CodeCopyBlock
        code={`.grid-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
}

.grid-item {
  background: lightgreen;
  padding: 20px;
  text-align: center;
}`}
      />
      <p>
        ✅ This layout adapts to screen size: items shrink or grow, and columns
        wrap automatically.
      </p>

      {/* Example 3: Media Query Grid */}
      <h2 className="section-title">3️⃣ Responsive Grid with Media Queries</h2>
      <CodeCopyBlock
        code={`.grid-container {
  display: grid;
  grid-template-columns: 1fr;
  gap: 15px;
}

@media (min-width: 600px) {
  .grid-container {
    grid-template-columns: 1fr 1fr;
  }
}

@media (min-width: 900px) {
  .grid-container {
    grid-template-columns: 1fr 1fr 1fr;
  }
}`}
      />
      <p>
        ✅ Uses media queries to switch from 1-column → 2-column → 3-column
        layouts.
      </p>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde047)",
          padding: "1.2rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#854d0e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Practice using <code>auto-fit</code> and <code>minmax()</code> for
            fluid grids.
          </li>
          <li>Experiment with combining Grid + Flexbox for hybrid layouts.</li>
          <li>Try building a responsive card layout or image gallery.</li>
          <li>Use dev tools to test how grids behave on different devices.</li>
        </ul>
      </div>
    </div>
  );
}
